import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, GeoJSON, useMapEvents, useMap, Popup } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Crosshair, Loader2, Search, X, Plus, Minus } from 'lucide-react';
import { MapLayer, api } from '../services/api';
import 'leaflet/dist/leaflet.css';

const pinIcon = L.divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
        <path d="M14 0 C6.268 0 0 6.268 0 14 C0 24.5 14 40 14 40 C14 40 28 24.5 28 14 C28 6.268 21.732 0 14 0 Z"
              fill="#6366f1" filter="drop-shadow(0 1px 2px rgba(0,0,0,0.3))"/>
        <circle cx="14" cy="14" r="5" fill="white"/>
    </svg>`,
    className: '',
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -40],
});

const isPointInPolygon = (lat: number, lng: number, polygon: number[][]): boolean => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const lngi = polygon[i][0], lati = polygon[i][1];
        const lngj = polygon[j][0], latj = polygon[j][1];
        const intersect = ((lati > lat) !== (latj > lat)) &&
            (lng < (lngj - lngi) * (lat - lati) / (latj - lati) + lngi);
        if (intersect) inside = !inside;
    }
    return inside;
};

const isPointInPolygonWithHoles = (lat: number, lng: number, rings: number[][][]): boolean => {
    if (!rings || rings.length === 0) return false;
    if (!isPointInPolygon(lat, lng, rings[0])) return false;
    for (let i = 1; i < rings.length; i++) {
        if (isPointInPolygon(lat, lng, rings[i])) return false;
    }
    return true;
};

const isPointInBoundary = (lat: number, lng: number, geojson: any): boolean => {
    if (!geojson || Object.keys(geojson).length === 0) return true;
    try {
        let polygonsWithRings: number[][][][] = [];
        if (geojson.type === 'FeatureCollection') {
            for (const f of geojson.features || []) {
                if (f.geometry?.type === 'Polygon') polygonsWithRings.push(f.geometry.coordinates);
                else if (f.geometry?.type === 'MultiPolygon') for (const p of f.geometry.coordinates) polygonsWithRings.push(p);
            }
        } else if (geojson.type === 'Feature') {
            if (geojson.geometry?.type === 'Polygon') polygonsWithRings.push(geojson.geometry.coordinates);
            else if (geojson.geometry?.type === 'MultiPolygon') for (const p of geojson.geometry.coordinates) polygonsWithRings.push(p);
        } else if (geojson.type === 'Polygon') polygonsWithRings.push(geojson.coordinates);
        else if (geojson.type === 'MultiPolygon') for (const p of geojson.coordinates) polygonsWithRings.push(p);
        if (polygonsWithRings.length === 0) return true;
        return polygonsWithRings.some(rings => isPointInPolygonWithHoles(lat, lng, rings));
    } catch { return true; }
};

interface GoogleMapsLocationPickerProps {
    apiKey?: string;
    townshipBoundary?: object | null;
    customLayers?: MapLayer[];
    defaultCenter?: { lat: number; lng: number };
    defaultZoom?: number;
    value?: { address: string; lat: number | null; lng: number | null };
    onChange: (location: { address: string; lat: number | null; lng: number | null }) => void;
    onAssetSelect?: (asset: { layerName: string; properties: Record<string, any>; lat: number; lng: number }) => void;
    onOutOfBounds?: () => void;
    onBoundaryChange?: (isOutside: boolean) => void;
    placeholder?: string;
    className?: string;
}

function MapClickHandler({ onMapClick }: { onMapClick: (lat: number, lng: number) => void }) {
    useMapEvents({ click(e) { onMapClick(e.latlng.lat, e.latlng.lng); } });
    return null;
}

function FitBounds({ bounds }: { bounds: L.LatLngBounds | null }) {
    const map = useMap();
    const fittedRef = useRef(false);
    useEffect(() => {
        if (bounds && bounds.isValid() && !fittedRef.current) {
            fittedRef.current = true;
            map.fitBounds(bounds, { padding: [20, 20] });
        }
    }, [bounds, map]);
    return null;
}

function CustomZoom() {
    const map = useMap();
    return (
        <div className="absolute bottom-14 left-3 z-[1000] flex flex-col gap-1">
            <button
                onClick={() => map.zoomIn()}
                className="w-9 h-9 bg-white/95 backdrop-blur-md rounded-xl border border-gray-200 shadow-lg flex items-center justify-center hover:bg-white transition-all active:scale-95"
                aria-label="Zoom in"
                type="button"
            >
                <Plus className="w-4 h-4 text-gray-700" />
            </button>
            <button
                onClick={() => map.zoomOut()}
                className="w-9 h-9 bg-white/95 backdrop-blur-md rounded-xl border border-gray-200 shadow-lg flex items-center justify-center hover:bg-white transition-all active:scale-95"
                aria-label="Zoom out"
                type="button"
            >
                <Minus className="w-4 h-4 text-gray-700" />
            </button>
        </div>
    );
}

interface SearchResult { display_name: string; lat: number; lng: number; }

export default function GoogleMapsLocationPicker({
    townshipBoundary,
    customLayers = [],
    defaultCenter = { lat: 4.189, lng: 73.513 },
    defaultZoom = 13,
    value,
    onChange,
    onAssetSelect,
    onOutOfBounds,
    onBoundaryChange,
    placeholder = 'Search for an address...',
    className = '',
}: GoogleMapsLocationPickerProps) {
    const [markerPos, setMarkerPos] = useState<[number, number] | null>(
        value?.lat && value?.lng ? [value.lat, value.lng] : null
    );
    const [searchInput, setSearchInput] = useState(value?.address || '');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLocating, setIsLocating] = useState(false);
    const [locateError, setLocateError] = useState<string | null>(null);
    const [isOutOfBounds, setIsOutOfBounds] = useState(false);
    const [boundaryBounds, setBoundaryBounds] = useState<L.LatLngBounds | null>(null);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (value?.address && value.address !== searchInput && value.address !== '') {
            setSearchInput(value.address);
        }
        if (value?.lat && value?.lng) setMarkerPos([value.lat, value.lng]);
    }, [value?.address, value?.lat, value?.lng]);

    useEffect(() => {
        if (!townshipBoundary) return;
        try {
            const gj = townshipBoundary as any;
            const bounds = L.latLngBounds([]);
            const addCoords = (coords: number[][]) => coords.forEach(([lng, lat]) => bounds.extend([lat, lng]));
            const extractGeom = (geom: any) => {
                if (!geom) return;
                if (geom.type === 'Polygon') geom.coordinates.forEach((r: number[][]) => addCoords(r));
                else if (geom.type === 'MultiPolygon') geom.coordinates.forEach((p: number[][][]) => p.forEach(r => addCoords(r)));
            };
            if (gj.type === 'FeatureCollection') gj.features?.forEach((f: any) => extractGeom(f.geometry));
            else if (gj.type === 'Feature') extractGeom(gj.geometry);
            else extractGeom(gj);
            if (bounds.isValid()) setBoundaryBounds(bounds);
        } catch { /* ignore */ }
    }, []);

    const handlePlace = useCallback(async (lat: number, lng: number, address?: string) => {
        const inBounds = isPointInBoundary(lat, lng, townshipBoundary);
        setIsOutOfBounds(!inBounds);
        if (!inBounds && onOutOfBounds) onOutOfBounds();
        if (onBoundaryChange) onBoundaryChange(!inBounds);
        setMarkerPos([lat, lng]);
        if (address) {
            setSearchInput(address);
            onChange({ address, lat, lng });
        } else {
            try {
                const result = await api.reverseGeocode(lat, lng);
                setSearchInput(result.formatted_address);
                onChange({ address: result.formatted_address, lat, lng });
            } catch {
                const addr = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
                setSearchInput(addr);
                onChange({ address: addr, lat, lng });
            }
        }
    }, [onChange, townshipBoundary, onOutOfBounds]);

    const handleSearch = useCallback(async (query: string) => {
        if (!query.trim()) { setSearchResults([]); return; }
        setIsSearching(true);
        try {
            const result = await api.geocodeAddress(query);
            if (result?.lat && result?.lng) {
                setSearchResults([{ display_name: result.formatted_address, lat: result.lat, lng: result.lng }]);
            } else { setSearchResults([]); }
        } catch { setSearchResults([]); }
        finally { setIsSearching(false); }
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchInput(val);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (val.length > 3) {
            searchTimeoutRef.current = setTimeout(() => handleSearch(val), 600);
        } else { setSearchResults([]); }
    };

    const handleLocate = () => {
        setLocateError(null);
        if (!('geolocation' in navigator)) {
            setLocateError('Geolocation is not supported by this browser.');
            return;
        }
        // Mobile browsers require a secure context (HTTPS or localhost) for geolocation.
        const isSecure = window.isSecureContext || ['localhost', '127.0.0.1'].includes(window.location.hostname);
        if (!isSecure) {
            setLocateError('Location requires a secure (HTTPS) connection. Please open the site over HTTPS.');
            return;
        }
        setIsLocating(true);
        const opts: PositionOptions = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setIsLocating(false);
                handlePlace(pos.coords.latitude, pos.coords.longitude);
            },
            (err) => {
                setIsLocating(false);
                let msg = 'Could not get your location.';
                if (err.code === err.PERMISSION_DENIED) {
                    msg = 'Location permission denied. Enable location access for this site in your browser settings.';
                } else if (err.code === err.POSITION_UNAVAILABLE) {
                    msg = 'Location unavailable. Make sure GPS / Location Services are turned on, then try again.';
                } else if (err.code === err.TIMEOUT) {
                    msg = 'Location request timed out. Move to an area with a better signal and try again.';
                }
                setLocateError(msg);
            },
            opts,
        );
    };

    const boundaryStyle: L.PathOptions = { color: '#6366f1', weight: 3, fillColor: '#6366f1', fillOpacity: 0.08 };
    const initialCenter: [number, number] = markerPos || [defaultCenter.lat, defaultCenter.lng];

    return (
        <div className={`relative flex flex-col gap-2 ${className}`}>
            <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                    {isSearching ? <Loader2 className="w-4 h-4 text-white/50 animate-spin" /> : <Search className="w-4 h-4 text-white/50" />}
                </div>
                <input
                    type="text"
                    value={searchInput}
                    onChange={handleInputChange}
                    onKeyDown={(e) => { if (e.key === 'Enter') { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); handleSearch(searchInput); } }}
                    placeholder={placeholder}
                    className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {searchInput && (
                    <button className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 z-10"
                        onClick={() => { setSearchInput(''); setSearchResults([]); }}>
                        <X className="w-4 h-4" />
                    </button>
                )}
                {searchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 z-[9999] bg-[#1e1e3f] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                        {searchResults.map((r, i) => (
                            <button key={i}
                                className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:bg-white/10 flex items-center gap-2 transition-colors"
                                onClick={() => { setSearchResults([]); handlePlace(r.lat, r.lng, r.display_name); }}>
                                <MapPin className="w-4 h-4 text-primary-400 shrink-0" />
                                <span className="truncate">{r.display_name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={handleLocate}
                    disabled={isLocating}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 text-white text-base font-bold shadow-lg shadow-primary-500/40 ring-2 ring-primary-300/50 hover:ring-primary-200 transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                    {isLocating ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        <Crosshair className="w-5 h-5" />
                    )}
                    {isLocating ? 'Locating…' : '📍 Use my current location'}
                </button>
                {isOutOfBounds && (
                    <span className="text-sm font-medium text-amber-300 bg-amber-500/10 px-3 py-1.5 rounded-lg border border-amber-500/30">
                        Selected location is outside the service area.
                    </span>
                )}
                {locateError && (
                    <span className="text-sm font-medium text-rose-200 bg-rose-500/10 px-3 py-1.5 rounded-lg border border-rose-500/40">
                        {locateError}
                    </span>
                )}
            </div>

            <div className="relative h-[28rem] sm:h-[32rem] md:h-[36rem] rounded-xl overflow-hidden border border-white/10">
                <MapContainer center={initialCenter} zoom={defaultZoom} className="h-full w-full" style={{ background: '#242424' }} zoomControl={false}>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitBounds bounds={markerPos ? null : boundaryBounds} />
                    <CustomZoom />
                    <MapClickHandler onMapClick={(lat, lng) => handlePlace(lat, lng)} />

                    {/* Service area boundary is enforced silently — not shown on public map */}

                    {customLayers.map((layer, i) => {
                        if (!layer.geojson) return null;
                        const gj = layer.geojson as any;
                        const features = gj.type === 'FeatureCollection' ? gj.features
                            : gj.type === 'Feature' ? [gj] : [];
                        return features.map((feat: any, j: number) => {
                            if (feat.geometry?.type !== 'Point') return null;
                            const [fLng, fLat] = feat.geometry.coordinates;
                            const props = feat.properties || {};
                            const assetIcon = L.divIcon({
                                html: `<div style="width:18px;height:18px;background:${layer.fill_color || '#6366f1'};border:2px solid white;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,0.4)"></div>`,
                                className: '',
                                iconSize: [18, 18],
                                iconAnchor: [9, 9],
                            });
                            return (
                                <Marker key={`layer-${i}-${j}`} position={[fLat, fLng]} icon={assetIcon}
                                    eventHandlers={{ click: () => onAssetSelect?.({ layerName: layer.name, properties: props, lat: fLat, lng: fLng }) }}>
                                    <Popup><b>{props.name || layer.name}</b></Popup>
                                </Marker>
                            );
                        });
                    })}

                    {markerPos && (
                        <Marker
                            position={markerPos}
                            icon={pinIcon}
                            draggable
                            eventHandlers={{
                                dragend: (e) => {
                                    const pos = (e.target as L.Marker).getLatLng();
                                    handlePlace(pos.lat, pos.lng);
                                },
                            }}
                        />
                    )}
                </MapContainer>

                <button
                    onClick={handleLocate}
                    className="absolute bottom-3 right-3 z-[1000] w-9 h-9 bg-white rounded-lg shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
                    title="Use my current location"
                >
                    {isLocating ? <Loader2 className="w-4 h-4 text-gray-600 animate-spin" /> : <Crosshair className="w-4 h-4 text-gray-600" />}
                </button>

                <p className="absolute bottom-3 left-3 z-[1000] text-xs bg-white/90 text-gray-600 px-2 py-1 rounded-lg shadow pointer-events-none select-none">
                    Click map or drag pin to set location
                </p>
            </div>

            {isOutOfBounds && (
                <div className="px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
                    <MapPin className="w-4 h-4 shrink-0" />
                    <span>This location is outside the service area boundary.</span>
                </div>
            )}
        </div>
    );
}
